import { Box, Button, LinkButton, Splash, Table } from '@salecycle/ui-component-library';
import { useEffect, useState } from 'react';
import { SubjectLineModal } from './SubjectLineModal';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { createSubjectLine, getSubjectLines, updateSubjectLine } from '../utils/queries';
import Spinner from 'core/ui/components/spinner';
import { getFirstNonEmptyValue } from 'sections/utils/objectHelpers';
import useNotifications from 'core/notifications/useNotifications';
import { ISubjectLine } from 'entities/subjectLine/subjectLine';
import { convertKeysToUppercase } from '../utils/convertKeysToUpperCase';

export const SubjectLines = () => {
  const { t } = useTranslation('designs');
  const { showNotification } = useNotifications();
  const { siteId } = useParams<{ siteId: string }>();
  const [showModal, setShowModal] = useState(false);
  const [currentSubjectLine, setCurrentSubjectLine] = useState(null);
  const [subjectLines, setSubjectLines] = useState([]);
  const { isLoading, isError, data } = useQuery(['subjectLines'], () => getSubjectLines({ siteId, t }));

  const create = useMutation(createSubjectLine);
  const update = useMutation(updateSubjectLine);

  useEffect(() => {
    if (!isLoading) {
      const dataCopy = [...data];
      dataCopy.forEach(element => {
        element.subjectLines = convertKeysToUppercase(element.subjectLines);
      });

      setSubjectLines(dataCopy ?? []);
    }
  }, [isLoading, data]);

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <Splash
        imageUrl="/images/error-plug-pink.svg"
        heading={t('designs:designGallery.subjectLines.queries.getSubjectLines.error')}
        imageHeight={150}
      />
    );
  }

  const addSubjectLine = () => {
    setCurrentSubjectLine(null);
    setShowModal(true);
  };

  const editSubjectLine = id => {
    setCurrentSubjectLine(id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentSubjectLine(null);
  };

  const onCreateSubjectLine = (subjectLine: ISubjectLine) => {
    create.mutate(
      {
        ...subjectLine,
        siteId,
        t,
      },
      {
        onSuccess: created => {
          showNotification(t('designs:designGallery.subjectLines.notifications.saveSuccess'), 'normal');
          setSubjectLines([...subjectLines, created]);
        },
        onError: () => {
          showNotification(t('designs:designGallery.form.errorHeading'), 'error');
        },
      },
    );
  };

  const onUpdateSubjectLine = (subjectLine: ISubjectLine) => {
    update.mutate(
      { ...subjectLine, audit: subjectLine.audit, t },
      {
        onSuccess: updated => {
          showNotification(t('designs:designGallery.subjectLines.notifications.saveSuccess'), 'normal');
          setSubjectLines([...subjectLines.filter(s => s.id !== subjectLine.id), updated]);
        },
        onError: () => {
          showNotification(t('designs:designGallery.form.errorHeading'), 'error');
        },
      },
    );
  };

  const saveSubjectLine = (subjectLine: ISubjectLine, isEdit: boolean) => {
    if (isEdit) {
      onUpdateSubjectLine(subjectLine);
    } else {
      onCreateSubjectLine(subjectLine);
    }
    closeModal();
  };

  const getColumns = () => {
    const SubjectLine = props => (
      <LinkButton onClick={() => editSubjectLine(props.original.id)}>
        {getFirstNonEmptyValue(props.original.subjectLines, 'en')}
      </LinkButton>
    );

    return [
      {
        Header: t('designGallery.subjectLines.columns.subjectLine'),
        id: 'subjectLine',
        accessor: d => getFirstNonEmptyValue(d.subjectLines, 'en'),
        Cell: SubjectLine,
      },
      {
        Header: t('designGallery.subjectLines.columns.languages'),
        id: 'languages',
        accessor: d =>
          Object.keys(d.subjectLines)
            .sort()
            .join(', '),
      },
    ];
  };

  return (
    <>
      <SubjectLineModal
        showModal={showModal}
        subjectLine={currentSubjectLine && subjectLines.find(s => s.id === currentSubjectLine)}
        onCloseButtonClicked={closeModal}
        onOkButtonClicked={saveSubjectLine}
      />
      <Box horizontalPositioning="end" margin="0 0 20px 0">
        <Button variation="primary" onClick={addSubjectLine}>
          Add subject line
        </Button>
      </Box>
      <Table columns={getColumns()} data={subjectLines} defaultSorted={[{ id: 'subjectLine', desc: false }]} />
    </>
  );
};
